import { IStandardCareProposal, ProductGroupType } from '@alberta/konexi-shared';
import { Inject, Injectable } from '@angular/core';
import { uniqWith } from 'lodash';
import { IntegratedCareProposalViewModel } from 'src/app/business/integrated-care/integrated-care-proposal';
import { map } from 'src/app/common/mapper/mapper';
import { StandardCareProposalDB } from 'src/app/common/repository/databases';
import { IQueryService } from 'src/app/shared/services/contracts/query/query-service';
import { QueryService } from 'src/app/shared/services/query/query.service';

import { TreeProductGroupViewModel } from '../../../business/product-group/tree-productgroup-viewmodel';
import { TenantConfigService } from '../tenant-config/tenant-config.service';
import { ProductGroupLoadService } from './product-group-load.service';

@Injectable()
export class ProductGroupTreeService {
  private _productGroupsLookUp: Record<string, TreeProductGroupViewModel> = {};
  private _hideGeneralContracts = false;
  constructor(
    @Inject(QueryService) private _queryService: IQueryService,
    private productGroupLoadService: ProductGroupLoadService,
    private _tenantConfigService: TenantConfigService
  ) {}

  public async loadProductGroupsFromStandardCareProposal(
    integratedCareProposal: IntegratedCareProposalViewModel
  ): Promise<TreeProductGroupViewModel[]> {
    const standardCareProposal = await this._queryService.get<IStandardCareProposal>(
      integratedCareProposal.standardCareProposalId,
      StandardCareProposalDB
    );
    const productGroups = await this.loadProductGroupIdsByStandardCareProposal(
      standardCareProposal,
      integratedCareProposal
    );
    this._hideGeneralContracts = await this._tenantConfigService.hideGeneralContracts();
    await this.buildTherapyTree(productGroups, integratedCareProposal, standardCareProposal);
    return this.buildProductGroupTree();
  }

  private async loadProductGroupIdsByStandardCareProposal(
    standardCareProposal: IStandardCareProposal,
    integratedCareProposal: IntegratedCareProposalViewModel
  ): Promise<TreeProductGroupViewModel[]> {
    const sortedProductGroupIds = standardCareProposal
      ? standardCareProposal.sortedProductGroupsWithMaxAmount.map(
          productGroupWithMaxAmount => productGroupWithMaxAmount.productGroupId
        )
      : this.getAllProductGroupsOfStandardCareProposals(integratedCareProposal);
    return this.productGroupLoadService.loadProductGroups(sortedProductGroupIds, productGroup =>
      map(productGroup, new TreeProductGroupViewModel())
    );
  }

  private getAllProductGroupsOfStandardCareProposals(
    integratedCareProposal: IntegratedCareProposalViewModel
  ): string[] {
    // TODO refactor or change
    if (!integratedCareProposal.allStandardCareProposals) {
      return [];
    }
    const sortedProductGroupsIds: string[] = [];

    integratedCareProposal.allStandardCareProposals.forEach(standardCareProposal => {
      standardCareProposal.sortedProductGroupsWithMaxAmount.forEach(productGroupWithQuantity => {
        if (
          !sortedProductGroupsIds.find(productGroupId => productGroupId === productGroupWithQuantity.productGroupId)
        ) {
          sortedProductGroupsIds.push(productGroupWithQuantity.productGroupId);
        }
      });
    });
    return sortedProductGroupsIds;
  }

  private async buildTherapyTree(
    productGroups: TreeProductGroupViewModel[],
    integratedCareProposal: IntegratedCareProposalViewModel,
    standardCareProposal: IStandardCareProposal
  ): Promise<void> {
    const productGroupsInStandardCareProposal: TreeProductGroupViewModel[] = [];

    for (const productGroup of productGroups) {
      productGroup.setBillingType(integratedCareProposal.possibleContracts);
      productGroup.setAllContractAreFlatCharge(integratedCareProposal.possibleContracts);
      if (this._hideGeneralContracts) {
        productGroup.hideGeneralContracts();
      }
      productGroupsInStandardCareProposal.push(productGroup);
    }

    await this.productGroupLoadService.loadProductGroupsNotInStandard(
      integratedCareProposal,
      productGroupsInStandardCareProposal,
      productGroup => map(productGroup, new TreeProductGroupViewModel())
    );

    productGroupsInStandardCareProposal.push(
      ...(await this.addNonDefaultProductGroupsByPredefinedArticles(
        integratedCareProposal,
        standardCareProposal,
        productGroupsInStandardCareProposal
      ))
    );

    uniqWith(productGroupsInStandardCareProposal, (a, b) => a._id === b._id)
      .filter(productGroup => productGroup.hasOwnProperty('type') && productGroup.type != null)
      .sort(this.productGroupSort)
      .map(productGroup => {
        productGroup.collapsed = true;
        return productGroup;
      })
      .map(productGroup => {
        this._productGroupsLookUp[productGroup._id] = productGroup;
        this._productGroupsLookUp[productGroup._id].children = [];
      });
  }

  private async addNonDefaultProductGroupsByPredefinedArticles(
    integratedCareProposal: IntegratedCareProposalViewModel,
    standardCareProposal: IStandardCareProposal,
    productGroupsInStandardCareProposal: TreeProductGroupViewModel[]
  ) {
    if (
      !standardCareProposal ||
      !standardCareProposal.predefinedArticles ||
      !standardCareProposal.predefinedArticles.length
    ) {
      return [];
    }

    const nonDefaultProductGroupIds = standardCareProposal.predefinedArticles
      .filter(
        predefinedArticle =>
          predefinedArticle.productGroupId &&
          !productGroupsInStandardCareProposal
            .map(treeProductGroup => treeProductGroup._id)
            .includes(predefinedArticle.productGroupId)
      )
      .map(predefinedArticle => {
        if (!integratedCareProposal.nonDefaultArticleProductGroupRelations[predefinedArticle.productGroupId]) {
          integratedCareProposal.nonDefaultArticleProductGroupRelations[predefinedArticle.productGroupId] = [
            predefinedArticle.articleId,
          ];
        } else {
          integratedCareProposal.nonDefaultArticleProductGroupRelations[predefinedArticle.productGroupId].push(
            predefinedArticle.articleId
          );
        }
        return predefinedArticle.productGroupId;
      });
    const nonDefaultProductGroups = await this.productGroupLoadService.loadProductGroups(
      nonDefaultProductGroupIds,
      productGroup => map(productGroup, new TreeProductGroupViewModel())
    );
    for (const productGroup of nonDefaultProductGroups) {
      productGroup.setBillingType(integratedCareProposal.possibleContracts);
      if (this._hideGeneralContracts) {
        productGroup.hideGeneralContracts();
      }
    }
    return nonDefaultProductGroups;
  }

  private buildProductGroupTree() {
    const productGroupTree: TreeProductGroupViewModel[] = [];
    for (const id in this._productGroupsLookUp) {
      if (this._productGroupsLookUp.hasOwnProperty(id)) {
        const productGroup = this._productGroupsLookUp[id];
        if (productGroup.parentId != null && this._productGroupsLookUp[productGroup.parentId]) {
          this._productGroupsLookUp[productGroup.parentId].children.push(productGroup);
          if (
            this._productGroupsLookUp[productGroup.parentId].children.some(child => child.type === ProductGroupType.WG)
          ) {
            this._productGroupsLookUp[productGroup.parentId].collapsed = false;
          }
        } else {
          productGroupTree.push(this._productGroupsLookUp[id]);
        }
      }
    }
    return productGroupTree.filter(productGroup => productGroup.type === ProductGroupType.OWG);
  }

  private productGroupSort(productGroup1: TreeProductGroupViewModel, productGroup2: TreeProductGroupViewModel) {
    return productGroup1.type === productGroup2.type
      ? productGroup1.name.localeCompare(productGroup2.name)
      : productGroup1.type.toString().localeCompare(productGroup2.type.toString());
  }
}
